import { Link } from 'react-router-dom';
import { useEffect, useState, memo, useMemo } from 'react';
import { imageOptimizer } from '../../../utils/imageOptimization';
import { useABTest } from '../../../hooks/useABTest';

const BULLETS = [
  {
    icon: 'ri-line-chart-line',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10 border-emerald-400/30',
    text: '+40% de leads qualifiés en moyenne dès le 1er mois',
  },
  {
    icon: 'ri-time-line',
    color: 'text-cyan-400',
    bg: 'bg-cyan-400/10 border-cyan-400/30',
    text: 'Réponses instantanées 24/7 — zéro recrutement, zéro formation',
  },
  {
    icon: 'ri-settings-3-line',
    color: 'text-violet-400',
    bg: 'bg-violet-400/10 border-violet-400/30',
    text: 'Déployé en moins de 10 minutes, intégré à vos outils existants',
  },
];

const TRUST_BADGES = [
  { icon: 'ri-shield-check-line', label: 'RGPD Conforme' },
  { icon: 'ri-customer-service-2-line', label: 'Support 7j/7' },
  { icon: 'ri-award-line', label: '+500 PME équipées' },
];

function Hero() {
  const [heroImage, setHeroImage] = useState<string>('');
  const heroCTATest = useABTest('hero_cta_test');
  const heroTitleTest = useABTest('hero_title_test');

  useEffect(() => {
    const imageUrl =
      'https://public.readdy.ai/ai/img_res/b339e4de87dc9b518bcfc6d23fd0f75a.jpg';
    imageOptimizer.getOptimizedImage(imageUrl).then(setHeroImage);
  }, []);

  useEffect(() => {
    if (heroTitleTest.isActive) heroTitleTest.trackImpression();
    if (heroCTATest.isActive) heroCTATest.trackImpression();
  }, [heroTitleTest.isActive, heroCTATest.isActive]);

  const handleCTAClick = () => {
    if (heroCTATest.isActive) {
      heroCTATest.trackConversion(true, { button: 'primary_cta' });
    }
  };

  const floatingAnimation = useMemo(() => ({
    animation: 'float 6s ease-in-out infinite'
  }), []);

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      aria-label="Hero Agent IA Pro"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyber-dark via-cyber-purple/20 to-cyber-blue/20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* ── LEFT CONTENT ── */}
          <div className="animate-fade-in-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-cyber-blue/30 mb-8 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white text-sm font-body">
                +500 PME ont déjà automatisé leur croissance
              </span>
            </div>

            {/* H1 — Titre principal orienté résultat */}
            <h1 className="font-display font-bold text-white mb-5 leading-tight">
              <span className="block text-5xl lg:text-6xl mb-2">
                Multipliez vos résultats
              </span>
              <span className="block text-5xl lg:text-6xl bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
                sans multiplier vos équipes.
              </span>
            </h1>

            {/* Sous-titre crédible */}
            <p className="text-lg text-gray-300 mb-8 font-body leading-relaxed max-w-xl">
              Agent IA Pro déploie des agents IA spécialisés — vente, support, marketing, RH —
              qui travaillent pour vous 24h/24, s&apos;intègrent à vos outils en quelques minutes
              et génèrent des résultats mesurables dès la première semaine.
            </p>

            {/* Bullet points différenciants */}
            <ul className="space-y-3 mb-10" aria-label="Avantages clés">
              {BULLETS.map((b, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm ${b.bg} animate-fade-in-up`}
                  style={{ animationDelay: `${0.4 + i * 0.15}s` }}
                >
                  <div className={`w-10 h-10 flex items-center justify-center shrink-0`}>
                    <i className={`${b.icon} ${b.color} text-xl`} />
                  </div>
                  <span className="text-gray-200 text-sm font-body">{b.text}</span>
                </li>
              ))}
            </ul>

            {/* CTA - Touch-friendly 44x44px minimum */}
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to="/signup"
                onClick={handleCTAClick}
                className="min-w-[44px] min-h-[48px] px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-body font-semibold rounded-full hover:shadow-2xl hover:shadow-cyber-blue/50 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center text-base"
                aria-label="Créer mon compte gratuitement"
              >
                Créer mon compte — Essai gratuit 14 jours
                <i className="ri-arrow-right-line ml-2 text-lg"></i>
              </Link>
              <Link
                to="/demo"
                className="min-w-[44px] min-h-[48px] px-8 py-4 bg-transparent border-2 border-white/40 text-white font-body font-semibold rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center text-base"
                aria-label="Voir une démo des agents IA"
              >
                Voir une démo live
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-6">
              {TRUST_BADGES.map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-400 font-body">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${badge.icon} text-cyber-blue text-base`} />
                  </div>
                  <span>{badge.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT VISUAL ── */}
          <div className="relative animate-fade-in-right">
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 blur-3xl animate-pulse-slow">
                <div className="w-full h-full bg-gradient-to-br from-cyber-blue/40 to-cyber-purple/40 rounded-full" />
              </div>

              {/* Image */}
              <div className="relative z-10 w-full h-[900px]" style={floatingAnimation}>
                {heroImage ? (
                  <img
                    src={heroImage}
                    alt="Agent IA Pro — interface intelligente pour PME"
                    className="w-full h-full object-contain drop-shadow-2xl"
                    width="800"
                    height="900"
                    loading="eager"
                    decoding="sync"
                    fetchpriority="high"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-3xl animate-pulse" />
                )}
              </div>

              {/* Orbiting icons */}
              <div className="absolute inset-0 z-0 animate-spin-slow">
                <div className="absolute top-1/4 left-0 w-16 h-16 bg-cyber-blue/20 backdrop-blur-xl border border-cyber-blue/40 rounded-2xl flex items-center justify-center shadow-xl">
                  <i className="ri-customer-service-2-line text-cyber-blue text-2xl" />
                </div>
              </div>

              <div className="absolute inset-0 z-0 animate-spin-reverse">
                <div className="absolute top-1/3 right-0 w-16 h-16 bg-cyber-purple/20 backdrop-blur-xl border border-cyber-purple/40 rounded-2xl flex items-center justify-center shadow-xl">
                  <i className="ri-shopping-cart-line text-cyber-purple text-2xl" />
                </div>
              </div>

              <div className="absolute inset-0 z-0 animate-spin-slower">
                <div className="absolute bottom-1/4 left-1/4 w-16 h-16 bg-teal-500/20 backdrop-blur-xl border border-teal-400/40 rounded-2xl flex items-center justify-center shadow-xl">
                  <i className="ri-mail-line text-teal-400 text-2xl" />
                </div>
              </div>

              {/* Floating KPIs */}
              <div className="absolute top-8 right-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 shadow-xl z-20 animate-float">
                <div className="text-emerald-400 text-3xl font-bold">+40%</div>
                <div className="text-white text-xs">Leads qualifiés</div>
              </div>

              <div className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 shadow-xl z-20 animate-float-delayed">
                <div className="text-cyber-blue text-3xl font-bold">10 min</div>
                <div className="text-white text-xs">Déploiement</div>
              </div>

              <div className="absolute top-1/2 right-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 shadow-xl z-20 animate-float-slow">
                <div className="text-violet-400 text-3xl font-bold">24/7</div>
                <div className="text-white text-xs">Disponibilité</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default memo(Hero);